import {
  ClientMetadata,
  CognitoUserSession,
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserAttribute
} from "amazon-cognito-identity-js";
import { Dispatch, SetStateAction } from "react";
import { create } from "zustand";
import {
  AUTH_MODE,
  COGNITO_CHALLENGE,
  ERRORS
} from "../consts";
import { updateUserInfo, type OnBoardingItemType } from "../queries/onboarding";
import {
  changePassword as awsChangePassword,
  completeCustomChallenge as awsCompleteCustomChallenge,
  completeNewPasswordChallenge as awsCompleteNewPasswordChallenge,
  confirmPassword as awsConfirmPassword,
  forgotPassword as awsForgotPassword,
  getApiCorporateInfo,
  getApiSessionToken,
  getApiUserData,
  getApiUserInfo,
  getSmartConfiguration,
  persistAuthStore,
  recoverAuthStore,
  refreshSessionCognito as awsRefreshSessionCognito,
  refreshSessionSSO as awsRefreshSessionSSO,
  seedSession as apiSeedSession,
  setAdminCookie,
  setApiOnBoardingCompleted,
  setApiPrivacyAgreements,
  setApiSessionInitiative,
  signIn as awsSignIn,
  signInSSO as awsSignInSSO,
  signOut as awsSignOut,
  storeJwtTokens,
  removeAdminCookie,
  retrieveExternalIdByUsername,
  validateQrSession,
  getApiUserImpersonation,
  startImpersonation,
  stopImpersonation,
  autoSubmitForm,
  getStatusRegistrationCheck,
  getFormStep
} from "../services/auth";
import { UserMgmtResponse } from "../services/fetchUserMgmt";
import i18n from "../services/i18n";
import { getMaintenanceStatus } from "../services/maintenance";
import type {
  AuthMode,
  CorporateInfo,
  SmartConfiguration, SmartConfigurationGeneric,
  UserData,
  UserInfo
} from "../types";
import { getError } from "../utils/error";
import {
  getCookieByName,
  getEnvironmentVariables,
  throttle
} from "../utils/general";
import { hasInvalidPath } from "../utils/login";

type RouterNavigation = {
  search?: string
  state?: Record<string, unknown>
  target: string
}

export type AuthState = {
  readonly allowedAuthentications: AuthMode[]
  corporateInfo: CorporateInfo | null
  error: Error | null /* errors should log out */
  /* the auth operations shouldn't stack, so a boolean should be fine */
  hasSeedSession:boolean
  isLoading: boolean
  isRefreshing: boolean
  isSeeding: {
    logout: boolean
    session: boolean
  }
  isUnAuthorized:boolean
  mode: AuthMode | null
  navigateTo: RouterNavigation | null // is this still useful?
  notice: string | null /* notices should not log out */
  session: CognitoUserSession | null
  sessionToken: string | null
  spinnerLoginVisible:boolean
  readonly ssoEndpoint: URL | string | null
  smartConfiguration: SmartConfiguration | null
  user: CognitoUser | null
  userData: UserData | null
  userInfo: UserInfo | null
  userMgmtData: UserMgmtResponse | null,
  splash:boolean
  maintenanceGlobal: boolean
  qrCodeValidate:boolean
  qrCodeValidateError: Error | null
  isImpersonation: boolean | undefined
  smartConfiguratorData: SmartConfigurationGeneric[] | undefined
  registrationCompletedVisible:boolean|null
  fromUrl: string | null
  showInitiativeEnrollmentWarning: boolean,
  profileImage: string | null
}

export type MutableAuthState = Pick<
  AuthState,
  | "corporateInfo"
  | "error"
  | "isLoading"
  | "isRefreshing"
  | "isSeeding"
  | "mode"
  | "navigateTo"
  | "notice"
  | "session"
  | "sessionToken"
  | "smartConfiguration"
  | "ssoEndpoint"
  | "user"
  | "userData"
  | "userInfo"
  | "userMgmtData"
  | "smartConfiguratorData"
  | "fromUrl"
  | "showInitiativeEnrollmentWarning"
  | "profileImage"
>

type AuthActions = {
  readonly changePassword:
    (oldPassword: string, newPassword: string) => Promise<boolean>
  readonly completeCustomChallenge:
    (
      challengeResponse: string,
      clientMetaData:ClientMetadata,
      redirect?: RouterNavigation,
      loadSpinner?:boolean) => void
  readonly completeNewPasswordChallenge:
    // (password: string, userAttributes: CognitoUserAttribute, redirect?: RouterNavigation) => void
    (password: string, userAttributes: CognitoUserAttribute) => Promise<void>
  readonly confirmPassword:
    (corporateId: number,
      username: string,
      newPassword: string,
      confirmationCode: string,
      ) => Promise<void>
  readonly forgotPassword: (corporateId: number, username: string,resend:boolean) => Promise<void>
  readonly postAgreementsConsent: () => Promise<void>
  readonly postOnBoardingCompleted: () => Promise<void>
  readonly putUpdateUserInfo: (industry: number, job: Pick<OnBoardingItemType, "id" | "name"> | null,
    profession: Pick<OnBoardingItemType, "id" | "name"> | null
  ) => Promise<void>
  readonly postInitiativeId: (initiativeId: string) => Promise<void>
  readonly stopImpersonation: () => Promise<void>
  readonly refetchUserInfo: () => Promise<void>
  readonly refreshSession: () => Promise<void>
  readonly resetError: () => void
  readonly resetNavigate: () => void
  readonly resetNotice: () => void
  readonly setUnAuthorized: (error: boolean) => void
  readonly setSessionSeeding: (loading: boolean) => void
  readonly setSpinnerLoginVisible: (loading: boolean) => void
  readonly seedLogout: (error: string | null, navigate: string | null) => Promise<void>
  readonly seedSession: (mode: keyof typeof AUTH_MODE, sessionToken: string) => Promise<void>
  readonly signIn: (corporateId: number, username: string, password: string, remember?: boolean, onSetIsLoading?: Dispatch<SetStateAction<boolean>>)
   => Promise<void>
  readonly signInSSO: (token: string, isAutoLogin?: boolean ) => Promise<void>
  readonly signOut: ( redirect?: string | null, isFromAdmin?: boolean, shouldSaveFromUrl?: boolean) => void
  readonly switchToInitiative: () => void,
  readonly resetSmartConfigurator: () => void,
  readonly setSplash: (arg: boolean) => void,
  readonly setFromUrl: (arg: string | null) => void,
  readonly setShowInitiativeEnrollmentWarning: (arg: boolean) => void,
  readonly setUserMgmtData: (data: UserMgmtResponse) => void,
  readonly setUserInfo: (data: UserInfo) => void,
  readonly setSmartConfiguratorData: (data: SmartConfigurationGeneric[]) => void,
  readonly resetQrValidation: ()=> void,
  readonly signOutAftereQrcodeValidation: ( redirect?: string | null, isFromAdmin?: boolean) => void,
  readonly setMaintenanceGlobal: (arg: boolean) => void,
  readonly setRegistrationCompletedVisible: (registrationCompletedVisible:boolean) => void,
  readonly setIsLoading: (arg:boolean) => void,
  readonly setProfileImage: (arg: string | null) => void
}

const {
  basePath,
  clientId,
  environment,
  ssoBaseUrl,
  ssoClientId,
  ssoRedirectUri
} = getEnvironmentVariables();

let allowedAuthentications: AuthMode[] = [];

if (clientId !== "no_set") {
  allowedAuthentications.push(AUTH_MODE.Cognito);
}

export const getParamState = (key: string)=>{
  let urlParams = new URLSearchParams(window.location.search);
  const stateParam = urlParams && decodeURIComponent(urlParams.get("state") || "") || "";

  // Se c'è un parametro state, proviamo a estrarre la chiave specifica
  if (stateParam) {
    // Gestiamo parametri multipli nel state separati da &
    const stateParams = new URLSearchParams(stateParam);
    const value = stateParams.get(key);
    if (value) {
      return decodeURIComponent(value);
    }

    // Fallback per compatibilità con il formato precedente
    if (stateParam.startsWith(`${key}=`)) {
      return decodeURIComponent(stateParam.replace(`${key}=`, ""));
    }
  }

  // Fallback: cerca direttamente nei parametri URL
  return urlParams.get(`${key}`) || "";
};

// Funzione helper per costruire URL SSO con fromUrl preservato
export const buildSSOUrlWithFromUrl = (baseEndpoint: URL | string | null, customFromUrl?: string | null): string => {
  if (!baseEndpoint) return "";

  // Assicurati che baseEndpoint sia un oggetto URL
  const urlEndpoint = typeof baseEndpoint === "string" ? new URL(baseEndpoint) : new URL(baseEndpoint.href);

  // Gestione del parametro state per preservare informazioni durante il flusso SSO
  const stateParams: string[] = [];

  // Recupera autoreg_code se presente
  const autoregCode = getParamState("autoreg_code");
  if (autoregCode) {
    stateParams.push(`autoreg_code=${encodeURIComponent(autoregCode)}`);
  }

  // Determina quale fromUrl usare
  let fromUrlToUse = customFromUrl;
  if (!fromUrlToUse) {
    // Cerca fromUrl nei parametri esistenti
    fromUrlToUse = getParamState("from_url") || new URLSearchParams(window.location.search).get("fromUrl");
  }
  if (!fromUrlToUse && window.location.pathname !== "/" && window.location.pathname !== "/access/login") {
    // Se non c'è fromUrl esplicito e l'utente è su una pagina specifica, salva l'URL corrente
    // ma solo se non è un path non valido (come logout, access, ecc.)
    const currentUrl = window.location.href;
    if (!hasInvalidPath(currentUrl)) {
      fromUrlToUse = currentUrl;
    }
  }

  // Verifica che il fromUrl finale sia valido prima di aggiungerlo
  if (fromUrlToUse && !hasInvalidPath(fromUrlToUse)) {
    stateParams.push(`from_url=${encodeURIComponent(fromUrlToUse)}`);
  }

  // Aggiungi il parametro state se ci sono parametri
  if (stateParams.length > 0) {
    urlEndpoint.searchParams.set("state", stateParams.join("&"));
  }

  return urlEndpoint.href.replace("%2B", "+");
};

const ssoUrl = "/oauth2/authorize";
const ssoResponseType = "code";
const ssoScope = "email openid profile";
let ssoEndpoint: URL | null = null;
if (ssoClientId !== "no_set" || ssoBaseUrl !== "no_set") { // ssoBaseUrl is checked for backwards compatibility..
  ssoEndpoint = new URL(ssoUrl, ssoBaseUrl);
  ssoEndpoint.searchParams.set("client_id", ssoClientId);
  ssoEndpoint.searchParams.set("response_type", ssoResponseType);
  ssoEndpoint.searchParams.set("scope", ssoScope);
  ssoEndpoint.searchParams.set("redirect_uri", ssoRedirectUri);

  // Aggiungi identity_provider se presente nell'URL corrente
  const urlParams = new URLSearchParams(window.location.search);
  const identityProvider = urlParams.get("identity_provider");
  if (identityProvider) {
    ssoEndpoint.searchParams.set("identity_provider", identityProvider);
  }

  allowedAuthentications.push(AUTH_MODE.SSO);
}

const defaultAuthState: AuthState = {
  allowedAuthentications,
  corporateInfo: null,
  error: null,
  hasSeedSession: false,
  isLoading: false,
  isRefreshing: false,
  isSeeding: {
    logout: false,
    session: false
  },
  isUnAuthorized: false,
  maintenanceGlobal: false,
  mode: null,
  splash: false,
  navigateTo: null,
  notice: null,
  session: null,
  sessionToken: null,
  smartConfiguration: null,
  spinnerLoginVisible: true,
  ssoEndpoint,
  user: null,
  userData: null,
  userInfo: null,
  userMgmtData: null,
  qrCodeValidate:false,
  qrCodeValidateError:null,
  isImpersonation: false,
  smartConfiguratorData: undefined,
  fromUrl: getParamState("from_url") || null,
  showInitiativeEnrollmentWarning: false,
  registrationCompletedVisible:null, // valori, 1) (true) altra tipologia login oppure utente già atenticato // Utente  in attesa del save dei dati a db  ,2) (false) login fallita , 3)(null) segui la solita logica
  profileImage: null
};

const storedAuthState = recoverAuthStore();

const initialAuthState = Object.assign({}, defaultAuthState, storedAuthState);

// Ci sono delle operazioni che avvengono subito dopo il signIn, usando il throttle alcuni stati non verranno salvati, senza scatenare errori (difficile da debuggare)
// per postInitiativeId (utenti con una sola iniziativa), l'initiative_id non veniva aggiornato perché partiva un set subito dopo il signin (entro 2 secondi).
// TODO: potrebbe verificarsi lo stesso problema per tutte le altre funzioni che usano throttledPersistAuthStore. Verificare.
const throttledPersistAuthStore =
  throttle((state: MutableAuthState, signOutFn?: () => void) => {
    persistAuthStore(state, signOutFn);
  }, 2000);


function getLanguageForApi(): string {
  const activeLang = getCookieByName("activeLangCode");
  if (activeLang) return activeLang;

  const cookieValue = getCookieByName("languageStored");
  if (cookieValue) {
    try {
      return JSON.parse(cookieValue).id || "";
    } catch { console.log("error getLanguageForApi")}
  }
  return document.documentElement.lang || "";
}

export const useAuthStore = create<AuthState & AuthActions>((set, get) => {
  function decodeJwt(token:string) {
    const [header, payload ] = token.split(".");
    const decodedHeader = JSON.parse(atob(header));
    const decodedPayload = JSON.parse(atob(payload));
    return {
      header: decodedHeader,
      payload: decodedPayload
    };
  }


  return {
    ...initialAuthState,
    /*** SIGN IN, SIGN OUT ***/
    postAgreementsConsent: async () => {
      try {
        //set({ isLoading: true });
        const sessionToken = get().sessionToken;

        const session = get().session;
        const accessToken = session?.getAccessToken().getJwtToken() ?? "";
        if (!sessionToken) {
          throw new Error("compromised auth state: sessionToken");
        }
        // se solo tornasse di suo il nuovo stato di userInfo...
        await setApiPrivacyAgreements({ accessToken, sessionToken });
        const newUserInfo = await getApiUserInfo({
          accessToken: session?.getAccessToken().getJwtToken() ?? "",
          sessionToken,
          language: getLanguageForApi()
        });
        set((state) => ({
          userInfo: {
            ...state.userInfo,
            ...newUserInfo
          },
          navigateTo: {
            target: state.fromUrl && state.fromUrl !== "" ? state.fromUrl : "/",
            firstAccess: true
          }
        }));

        persistAuthStore(get(), () => {
          get().signOut("/access/login", false, false);
        });

      } catch (error) {
        if (error !== ERRORS.UNAUTHORIZED) {
          // eslint-disable-next-line no-console
          console.error("postAgreementsConsent", error);
          set({ error: getError(error)?.[0] });
        }
      }
    },
    stopImpersonation: async () => {
      const newState = { ...get() };
      try {
        newState.isLoading = true;
        set({
          isLoading: newState.isLoading
        });
        if (!newState.session) {
          throw new Error("compromised auth state: session");
        }
        if (!newState.sessionToken) {
          throw new Error("compromised auth state: sessionToken");
        }
        const userData = await stopImpersonation({
          accessToken: newState.session?.getAccessToken().getJwtToken(),
          sessionToken : newState.sessionToken
        });

        const newUserInfo = await getApiUserInfo({
          accessToken: newState.session?.getAccessToken().getJwtToken(),
          sessionToken : newState.sessionToken,
          language: getLanguageForApi()
        });

        newState.userInfo = newUserInfo;
        newState.userData = userData;
      } catch (error) {
        if (error !== ERRORS.UNAUTHORIZED) {
          // eslint-disable-next-line no-console
          console.error("postInitiativeId", error);
          newState.error = getError(error)?.[0] ?? null;
        }
      } finally {
        newState.isLoading = false;
        // set({
        //   ...newState,
        //   userData: { ...newState.userData as UserData },
        //   userInfo: { ...newState.userInfo as UserInfo }
        // });

        // 🚨 GESTIONE ERRORE persistAuthStore con logout automatico in caso di errore
        persistAuthStore(newState, () => {
          get().signOut("/access/login", false, false);
        });
      }
    },
    postInitiativeId: async (initiativeId: string) => {
      const newState = { ...get() };
      try {
        newState.isLoading = true;
        set({
          isLoading: newState.isLoading
        });
        if (!newState.session) {
          throw new Error("compromised auth state: session");
        }
        if (!newState.sessionToken) {
          throw new Error("compromised auth state: sessionToken");
        }

        const userImpersonation = await getApiUserImpersonation({
          accessToken: newState.session?.getAccessToken().getJwtToken(),
          sessionToken : newState.sessionToken });

        newState.isImpersonation = userImpersonation.isImpersonation;

        const initiatives = newState.userInfo?.initiatives.map(item => {
          return item.id;
        });

        newState.sessionToken = await setApiSessionInitiative({
          accessToken: newState.session?.getAccessToken().getJwtToken(),
          authorization: newState.session?.getIdToken().getJwtToken(),
          initiativeId,
          refreshToken: newState.session?.getRefreshToken().getToken(),
          sessionToken: newState.sessionToken,
          initiatives
        });
        if (userImpersonation.isImpersonation) {
          document.cookie = `isImpersonation=${true}`;

          newState.userData = await startImpersonation({
            accessToken: newState.session?.getAccessToken().getJwtToken(),
            sessionToken : newState.sessionToken,
            corporateId: userImpersonation.user.organization_id,
            external_id: userImpersonation.user.external_id,
            initiativeId
          });

          
          newState.corporateInfo = await getApiCorporateInfo({
            accessToken: newState.session?.getAccessToken().getJwtToken(),
            corporateId: userImpersonation.user.organization_id,
            sessionToken: newState.sessionToken
          });

        } else {
          const userData = await getApiUserData({
            accessToken: newState.session?.getAccessToken().getJwtToken(),
            sessionToken: newState.sessionToken
          });
          newState.userData = userData.user;
          newState.isImpersonation = userData.isImpersonation;
        }

        if (newState.userData.initiative_id == null || newState.userData.organization_id == null) {
          throw new Error("compromised auth state: userData");
        }
        newState.smartConfiguration = await getSmartConfiguration({
          accessToken: newState.session.getAccessToken().getJwtToken(),
          corporateId: newState.userData.organization_id,
          initiativeId: newState.userData.initiative_id,
          sessionToken: newState.sessionToken
        });

        const maintenanceParams = {
          idToken: newState.session?.getAccessToken().getJwtToken() ?? "",
          sessionToken: newState.sessionToken,
          userRoles: newState.userData?.roles
        };
        const isMaintenanceEnabled = await getMaintenanceStatus(maintenanceParams);
        newState.maintenanceGlobal = isMaintenanceEnabled;

      } catch (error) {
        if (error !== ERRORS.UNAUTHORIZED) {
          // eslint-disable-next-line no-console
          console.error("refetchSmartConfiguration", error);
          newState.error = getError(error)?.[0] ?? null;
        }
      } finally {
        if (
          newState.session && newState.sessionToken
          && newState.corporateInfo?.corporate_domain
        ) {
          setAdminCookie({
            accessToken: newState.session.getAccessToken().getJwtToken(),
            authorization: newState.session.getIdToken().getJwtToken(),
            domain: newState.corporateInfo.corporate_domain,
            sessionId: newState.sessionToken
          });
        } else if (newState.corporateInfo?.corporate_domain) {
          removeAdminCookie({ domain: newState.corporateInfo.corporate_domain });
        }
        newState.isLoading = false;
        let mainUrl = newState.smartConfiguration?.visForyou === false ? "/esplora" : "/per-te";
        if (newState.fromUrl){
          mainUrl = newState.fromUrl;
        }
        newState.navigateTo = { target: mainUrl };
        set({
          ...newState,
          userData: { ...newState.userData as UserData }
        });

        // 🚨 GESTIONE ERRORE persistAuthStore con logout automatico in caso di errore
        persistAuthStore(newState, () => {
          get().signOut("/access/login", false, false);
        });
      }
    },
    setMaintenanceGlobal: (isMaintenanceGlobal: boolean) => {
      set({ maintenanceGlobal: isMaintenanceGlobal });
    },
    setFromUrl: (fromUrl: string|null) => {
      set({ fromUrl });
    },
    setShowInitiativeEnrollmentWarning: (showInitiativeEnrollmentWarning: boolean) => {
      set({ showInitiativeEnrollmentWarning });
    },
    setSplash: (paramSplash: boolean) => {
      set({ splash: paramSplash });
    },
    setUserMgmtData: (data: UserMgmtResponse) => {
      set({ userMgmtData: data });
    },
    setProfileImage: (data: string | null) => {
      set({ profileImage: data });
    },
    setUserInfo: (data: UserInfo) => {
      set({ userInfo: data });
    },
    setSmartConfiguratorData: (data: SmartConfigurationGeneric[]) => {
      set({ smartConfiguratorData: data });
    },
    postOnBoardingCompleted: async () => {
      try {
        set({ isLoading: true });
        const sessionToken = get().sessionToken;
        const session = get().session;
        if (!sessionToken) {
          throw new Error("compromised auth state: sessionToken");
        }
        await setApiOnBoardingCompleted({ authorization: session?.getAccessToken().getJwtToken() ?? "", sessionToken });
        const newUserInfo = await getApiUserInfo({
          accessToken: session?.getAccessToken().getJwtToken() ?? "",
          sessionToken,
          language: getLanguageForApi()
        });
        set({ userInfo: newUserInfo });
      } catch (error) {
        if (error !== ERRORS.UNAUTHORIZED) {
          // eslint-disable-next-line no-console
          console.error("postOnBoardingCompleted", error);
          set({ error: getError(error)?.[0] });
        }
      } finally {
        const newState = { ...get() };
        newState.isLoading = false;
        set({ isLoading: false });
        throttledPersistAuthStore(newState, get().signOut);
        set(newState);
      }
    },
    putUpdateUserInfo: async (
      industry: number,
      job: Pick<OnBoardingItemType, "id" | "name"> | null,
      profession: Pick<OnBoardingItemType, "id" | "name"> | null
    ) => {
      try {
        set({ isLoading: true });
        const sessionToken = get().sessionToken;
        const session = get().session;
        if (!sessionToken) {
          throw new Error("compromised auth state: sessionToken");
        }
        await updateUserInfo({
          authorization: session?.getAccessToken().getJwtToken() ?? "",
          industry: industry,
          job: job,
          profession: profession,
          sessionToken,
          language: getLanguageForApi()
        });
        const newUserInfo = await getApiUserInfo({
          accessToken: session?.getAccessToken().getJwtToken() ?? "",
          sessionToken,
          language: getLanguageForApi()
        });
        set({ userInfo: newUserInfo });
      } catch (error) {
        if (error !== ERRORS.UNAUTHORIZED) {
          // eslint-disable-next-line no-console
          console.error("postOnBoardingCompletedO", error);
          set({ error: getError(error)?.[0] });
        }
      } finally {

        set({ isLoading: false });
        const smartConfiguration = get().smartConfiguration;

        if (smartConfiguration?.urlDomain
          && smartConfiguration?.urlCorporateDomain
          && environment !== "LOCAL"
        ) {
          window.location.href =
            // eslint-disable-next-line max-len
            `https://${smartConfiguration.urlDomain}.${smartConfiguration.urlCorporateDomain}/signup/skills`;
        }
        else {
          window.location.href =
            // eslint-disable-next-line max-len
            `${window.location.origin}/signup/skills`;
        }


      }
    },
    signIn: async (corporateId, username, password, remember = false, onSetIsLoading) => {
      const newState = { ...get() };
      if (remember && username) {
        localStorage.setItem("storedUser", username);
      } else {
        localStorage.removeItem("storedUser");
      }
      try {
        newState.isLoading = true;
        set({ isLoading: newState.isLoading });
        const { external_id: externalId } = await retrieveExternalIdByUsername({ corporateId, username });
        const {
          session: newSession,
          user: newUser
        } = await awsSignIn(externalId, password);
        newState.mode = AUTH_MODE.Cognito;
        newState.session = newSession;
        newState.user = newUser;
        newState.sessionToken = await getApiSessionToken({
          accessToken: newState.session.getAccessToken().getJwtToken(),
          authorization: newState.session.getIdToken().getJwtToken(),
          refreshToken: newState.session.getRefreshToken().getToken()
        });
        const userData = await getApiUserData({
          accessToken: newState.session?.getAccessToken().getJwtToken(),
          sessionToken: newState.sessionToken
        });
        newState.userData = userData.user;
        newState.isImpersonation = userData.isImpersonation;
        newState.userInfo = await getApiUserInfo({
          accessToken: newState.session?.getAccessToken().getJwtToken(),
          sessionToken: newState.sessionToken,
          language: getLanguageForApi()
        });

        // eslint-disable-next-line no-console
        console.log("signIn: tentativo recupero corporateInfo per organization_id:", newState.userData?.organization_id);

        newState.corporateInfo = await getApiCorporateInfo({
          accessToken: newState.session?.getAccessToken().getJwtToken(),
          corporateId: newState.userData?.organization_id,
          sessionToken: newState.sessionToken
        });

        // eslint-disable-next-line no-console
        console.log("signIn: corporateInfo recuperato:", newState.corporateInfo ? "SUCCESS" : "FAILED");

        // SSO check se il portale è in MAINTEINANCE MODE GLOBALE , NESSUNO PROFLO PUO ACCEDERE TRANNE PO
        const maintenanceParams = {
          idToken: newState.session?.getAccessToken().getJwtToken() ?? "",
          sessionToken: newState.sessionToken,
          userRoles: userData?.user?.roles
        };
        const isMaintenanceEnabled = await getMaintenanceStatus(maintenanceParams);
        newState.maintenanceGlobal = isMaintenanceEnabled;


        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        if (error.type === COGNITO_CHALLENGE.NEW_PASSWORD_REQUIRED) {
          // go to privacy notice
          newState.user = error.user;
          newState.navigateTo = { // not valid anymore
            state: {
              firstAccess: {
                oldPassword: password,
                userAttributes: error.userAttributes,
                username: newState.user?.getUsername()
              }
            },
            target: "/access/password"
          };
        } else if (error.type === COGNITO_CHALLENGE.CUSTOM_CHALLENGE) {
          // go to otp form
          newState.user = error.user;
          newState.navigateTo = {
            state: {
              firstAccess: {
                oldPassword: password,
                userAttributes: error.userAttributes,
                username: newState.user?.getUsername()
              }
            },
            target: "/access/password/code"
          };
          // } else if (error === ERRORS.UNAUTHORIZED) {
          //   get().refreshSession();
        } else {
          // eslint-disable-next-line no-console
          console.error("signIn Error:", error);
          newState.error = getError(error)?.[0] ?? null;
        }
      } finally {
        onSetIsLoading && onSetIsLoading(false);
        if (newState.error) {
          newState.error && set({ error: getError(newState.error)?.[0] ?? null });
          get().signOut("/access/login");
        } else {
          if (
            newState.session && newState.sessionToken
              && newState.corporateInfo?.corporate_domain
          ) {
            setAdminCookie({
              accessToken: newState.session.getAccessToken().getJwtToken(),
              authorization: newState.session.getIdToken().getJwtToken(),
              domain: newState.corporateInfo.corporate_domain,
              sessionId: newState.sessionToken
            });
          } else if (newState.corporateInfo?.corporate_domain) {
            removeAdminCookie({ domain: newState.corporateInfo.corporate_domain });
          }
          newState.isLoading = false;
          set(newState);

          throttledPersistAuthStore(newState, get().signOut);
        }
      }
    },
    signInSSO: async (token, isAutoLogin = false) => {
      const uuidQr = localStorage.getItem("uuid_qrcode");
      const newState = get();
      const autoregCode = getParamState("autoreg_code");
      const fromUrl = getParamState("from_url");
      // TODO parametro recuperato da idp, recuperare anche se ho già sessione in localStorage
      newState.isLoading = true;
      newState.fromUrl = fromUrl;
      // ❌ RIMOSSO: set({ fromUrl: newState.fromUrl || null, isLoading: newState.isLoading });
      // Questo set() scatenava prematuramente il useEffect di Root.page.tsx prima che le iniziative fossero caricate
      // Ora il set() avviene solo alla fine quando tutti i dati sono pronti
      set({ isLoading: newState.isLoading }); // Solo loading, fromUrl verrà salvato nel set finale
      let registrationCheck:string = "";
      let registrationCompletedVisible = null;

      const isRegistered = ()=>{
        return (autoregCode && registrationCheck === "REGISTERED") || !autoregCode;
      };

      try {
        const {
          tokens: jwtTokens,
          user: newUser
        } = await awsSignInSSO({
          baseUrl: ssoBaseUrl,
          clientId: ssoClientId,
          redirectUri: ssoEndpoint?.searchParams.get("redirect_uri")!,
          token
        });
        newState.mode = AUTH_MODE.SSO;
        newState.user = newUser;
        storeJwtTokens(jwtTokens);

        const session = new CognitoUserSession({
          AccessToken: new CognitoAccessToken({ AccessToken: jwtTokens.access_token }),
          IdToken: new CognitoIdToken({ IdToken: jwtTokens.id_token }),
          RefreshToken: new CognitoRefreshToken({ RefreshToken: jwtTokens.refresh_token })
        });

        let userMgmtData = get().userMgmtData;
        if (!userMgmtData || !userMgmtData.id || userMgmtData.id === 0) {
          newState.error = new Error(i18n.t("corporate_not_found"));
          set({ error: newState.error });
          return;
        }
        const idCorporate = userMgmtData.id;

        // se nell'url è presente l'autoreg_code chiamo il servizio "getStatusRegistrationCheck" per verificare lo stato di registrazione
        if(autoregCode){
          const decodedData = decodeJwt(jwtTokens.id_token);

          registrationCheck = await getStatusRegistrationCheck({
            corporateId:idCorporate.toString(),
            externalId:decodedData.payload["custom:ExternalID"]
          });
        }

        // se il parametro  autoreg_Code NON è presente nell'url o l'utente è già registrato eseguo la solita logica
        if(isRegistered()){

          newState.session = session;

          newState.sessionToken = await getApiSessionToken({
            accessToken: session.getAccessToken().getJwtToken(),
            authorization: session.getIdToken().getJwtToken(),
            refreshToken: session.getRefreshToken().getToken()
          });

          const userData = await getApiUserData({
            accessToken: session?.getAccessToken().getJwtToken()||"",
            sessionToken: newState.sessionToken
          });
          newState.userData = userData.user;
          newState.isImpersonation = userData.isImpersonation;
          newState.userInfo = await getApiUserInfo({
            accessToken: session?.getAccessToken().getJwtToken(),
            sessionToken: newState.sessionToken,
            language: getLanguageForApi()
          });

          // eslint-disable-next-line no-console
          console.log("signInSSO: tentativo recupero corporateInfo per organization_id:", newState.userData?.organization_id);

          newState.corporateInfo = await getApiCorporateInfo({
            accessToken: session?.getAccessToken().getJwtToken(),
            corporateId: newState.userData?.organization_id,
            sessionToken: newState.sessionToken
          });

          // eslint-disable-next-line no-console
          console.log("signInSSO: corporateInfo recuperato:", newState.corporateInfo ? "SUCCESS" : "FAILED");


          if(uuidQr && newState.session?.getAccessToken().getJwtToken() &&
            session.getIdToken().getJwtToken() &&
            session.getRefreshToken().getToken()){
            newState.qrCodeValidate = await validateQrSession({
              uuid: uuidQr,
              authToken: session.getAccessToken().getJwtToken(),
              idToken: session.getIdToken().getJwtToken(),
              refreshToken: session.getRefreshToken().getToken()
            });
          }


          // SSO check se il portale è in MAINTEINANCE MODE GLOBALE , NESSUNO PROFLO PUO ACCEDERE TRANNE PO
          const maintenanceParams = {
            idToken: newState.session?.getAccessToken().getJwtToken() ?? "",
            sessionToken: newState.sessionToken,
            userRoles: userData?.user?.roles
          };
          const isMaintenanceEnabled = await getMaintenanceStatus(maintenanceParams);
          newState.maintenanceGlobal = isMaintenanceEnabled;

          // se l'utente risulta in registrazione eseguo il redirect alla pagina di login con succes e messaggio "vi arriverà un email ecc ecc"
        }else if(registrationCheck === "PARTIAL_REGISTRATION"){
          registrationCompletedVisible = true;
          //se l'utente risulta NON registrato  viene eseguita la chiamata SUBMIT con tutte le info prese dal token
        }else if(registrationCheck === "NOT_REGISTERED"){

          try {
            const jwtToken = jwtTokens.id_token;
            const decodedData = decodeJwt(jwtToken);

            const formType = get().userMgmtData?.self_signup?.toLowerCase() ||"";

            const formDataInfo = await getFormStep({
              idCorporate:idCorporate.toString(),
              formType:`signup-${formType}`
            });

            // Creo un il payload da inviare al submit in base alle chiavi ricevute da getFormStep
            const getAllNecessaryKeys = formDataInfo?.steps?.map(el => el.fields).flat().map(el => el.key);
            let dataObject =  Object.assign(
              { code: autoregCode, external_employee_flag: false }, // accesso con credenziali applicative false
              ...getAllNecessaryKeys.map(el => {
                if (el === "surname" && (decodedData.payload["family_name"] || decodedData.payload["surname"])) {
                  return { surname: (decodedData.payload["family_name"] || decodedData.payload["surname"]) };
                } else if(decodedData.payload[el]){
                  return { [el]: decodedData.payload[el] };
                }else{
                  return;
                }
              }));

            const isSubmitted = await autoSubmitForm({
              idCorporate,
              formType:`signup-${formType}`,
              data:dataObject });

            if(isSubmitted){
              registrationCompletedVisible = true;
            }
          }catch (errWithAutoregCode) {
            registrationCompletedVisible = false;
            throw errWithAutoregCode;
          }

        }
      } catch (error) {
        newState.error = getError(error)?.[0] ?? null;
        newState.error && set({ error: getError(newState.error)?.[0] ?? null });
      } finally {
        if (newState.error) {
          newState.error && set({ error: getError(newState.error)?.[0] ?? null });
          if (isAutoLogin) {
            // Se è un autologin e fallisce, reindirizza a sso-redirect-error per prevenire loop infinito
            set({ isLoading: false }); // Pulisci lo stato di loading prima del redirect
            window.location.href = "/sso-redirect-error";
          } else {
            // Solo per login manuali, procedi con il signOut normale
            isRegistered() && get().signOut();
          }
        } else {
          if (!uuidQr) {
            if (
              newState.session && newState.sessionToken
                && newState.corporateInfo?.corporate_domain
            ) {
              setAdminCookie({
                accessToken: newState.session.getAccessToken().getJwtToken(),
                authorization: newState.session.getIdToken().getJwtToken(),
                domain: newState.corporateInfo.corporate_domain,
                sessionId: newState.sessionToken
              });
            } else if (newState.corporateInfo?.corporate_domain) {
              removeAdminCookie({ domain: newState.corporateInfo.corporate_domain });
            }
          } else {
            get().signOutAftereQrcodeValidation();
          }
        }
        newState.isLoading = false;

        newState.registrationCompletedVisible = registrationCompletedVisible;

        // Verifica che corporateInfo sia presente prima del salvataggio
        if (!newState.corporateInfo && newState.session && newState.sessionToken && newState.userData?.organization_id) {
          // eslint-disable-next-line no-console
          console.warn("signInSSO: corporateInfo mancante, tento il recupero automatico prima del salvataggio");

          try {
            const recoveredCorporateInfo = await getApiCorporateInfo({
              accessToken: newState.session.getAccessToken().getJwtToken(),
              corporateId: newState.userData.organization_id,
              sessionToken: newState.sessionToken
            });
            newState.corporateInfo = recoveredCorporateInfo;
            // eslint-disable-next-line no-console
            console.log("signInSSO: corporateInfo recuperato con successo nel retry");
          } catch (retryError) {
            // eslint-disable-next-line no-console
            console.error("signInSSO: fallito anche il retry per corporateInfo:", retryError);
          }
        }

        newState.isLoading = false;
        set(newState);
        throttledPersistAuthStore(newState, get().signOut);
      }
    },
    setRegistrationCompletedVisible: (registrationCompletedVisible: boolean) => {
      set({ registrationCompletedVisible });
    },
    setIsLoading: (isLoading: boolean) => {
      set({ isLoading: isLoading });
    },
    signOut: (redirect: string | null = "/", isFromAdmin?: boolean, shouldSaveFromUrl?: boolean) => {
      // get auth store data
      const authMode = get().mode;
      const error = get().error;

      // alternative would be to check each error type in the finally statements
      const isRefreshing = get().isRefreshing;
      const smartConfiguration = get().smartConfiguration;
      // remove local storage
      awsSignOut({
        authorization: get().session?.getAccessToken().getJwtToken() ?? null,
        baseUrl: basePath,
        baseUrlSSO: ssoBaseUrl,
        clientIdSSO: ssoClientId,
        deleteSession: !isRefreshing,
        domain: get().corporateInfo?.corporate_domain,
        mode: authMode,
        refreshToken: get().session?.getRefreshToken() ?? null,
        sessionToken: get().sessionToken
      })
        .finally(() => {
          // reset auth store data: the error and navigation will be handled as query params
          const storedUser = localStorage.getItem("storedUser");
          localStorage.clear();

          if (storedUser) {
            localStorage.setItem("storedUser", storedUser);
          }

          set({ ...defaultAuthState,spinnerLoginVisible: false });
          // redirect to corporate domain for logout (if necessary)

          const urlParams = new URLSearchParams(window.location.search);
          const codeParam = urlParams.get("code");

          if(import.meta.env.VITE_LOGOUT_REDIRECT_URI && import.meta.env.VITE_LOGOUT_REDIRECT_URI !== "") {
            return window.location.href = import.meta.env.VITE_LOGOUT_REDIRECT_URI;
          }

          if (codeParam){
            redirect = "/access/login";
          }

          if (redirect || shouldSaveFromUrl) {

            // Controlla se esiste già un parametro fromUrl nei query params attuali
            const currentUrlParams = new URLSearchParams(window.location.search);
            const existingFromUrl = currentUrlParams.get("fromUrl");

            // Determina quale fromUrl usare:
            // - Se shouldSaveFromUrl è true, usa l'URL corrente
            // - Altrimenti, se esiste già un fromUrl nei query params, mantienilo
            const fromUrlToUse = shouldSaveFromUrl
              ? window.location.href
              : existingFromUrl;

            set({ fromUrl: fromUrlToUse });

            // Costruisce i parametri query per il redirect
            const buildQueryParams = () => {
              return Object.entries({
                error: error?.message ?? null,
                navigate: window.location.pathname !== redirect ? redirect : null,
                ...(fromUrlToUse && { fromUrl: fromUrlToUse })
              })
                .filter(([, value]) => value != null)
                .map(([label, value]) => `${label}=${value}`)
                .join("&");
            };

            // Determina se è necessario un redirect cross-domain (da iniziativa a corporate)
            const shouldRedirectToCorporateDomain = () => {
              return environment !== "LOCAL"
                && smartConfiguration?.urlCorporateDomain
                && window.location.host !== smartConfiguration.urlCorporateDomain;
            };

            // Costruisce l'URL completo con parametri
            const buildUrlWithParams = (baseUrl: string, params: string) => {
              return `${baseUrl}${params.length ? `?${params}` : ""}`;
            };

            const params = buildQueryParams();

            if (shouldRedirectToCorporateDomain()) {
              // Scenario 1: Redirect cross-domain da iniziativa a corporate
              const corporateLogoutUrl = `https://${smartConfiguration!.urlCorporateDomain}/logout`;
              const finalUrl = buildUrlWithParams(corporateLogoutUrl, params);
              window.location.href = finalUrl;
            } else if (redirect) {
              // Scenario 2: Redirect stesso dominio con navigazione gestita da store
              const currentUrl = new URL(window.location.href);
              const fromUrlParam = currentUrl.searchParams.get("fromUrl");

              // Unifica la logica fromUrl: priorità a Zustand store, fallback a query params
              let finalFromUrl = null;

              if (shouldSaveFromUrl) {
                // Caso 1: Salva URL corrente come fromUrl (solo se valido)
                const currentUrl = window.location.href;
                if (!hasInvalidPath(currentUrl)) {
                  finalFromUrl = currentUrl;
                } else {
                  console.log("Skipping invalid current URL for fromUrl:", currentUrl);
                }
              } else {
                // Caso 2: Usa fromUrl esistente (priorità a store, fallback a query params)
                const storeFromUrl = get().fromUrl;
                const candidateFromUrl = storeFromUrl || fromUrlParam;

                // Verifica che il fromUrl candidato non abbia path non validi
                if (candidateFromUrl && !hasInvalidPath(candidateFromUrl)) {
                  finalFromUrl = candidateFromUrl;
                } else if (candidateFromUrl) {
                  console.log("Skipping invalid fromUrl:", candidateFromUrl);
                  // Se il candidato non è valido, prova a estrarre il fromUrl annidato dai query params
                  try {
                    const candidateUrl = new URL(candidateFromUrl);
                    const nestedFromUrl = candidateUrl.searchParams.get("fromUrl");
                    if (nestedFromUrl && !hasInvalidPath(nestedFromUrl)) {
                      finalFromUrl = decodeURIComponent(nestedFromUrl);
                      console.log("Extracted valid nested fromUrl:", finalFromUrl);
                    }
                  } catch (error) {
                    console.log("Failed to extract nested fromUrl from:", candidateFromUrl);
                  }
                }
              }

              console.log("Same-domain redirect logic:", {
                shouldSaveFromUrl,
                storeFromUrl: get().fromUrl,
                fromUrlParam,
                finalFromUrl,
                isFromAdmin
              });

              if (isFromAdmin) {
                // Caso admin: naviga direttamente al login con fromUrl come state
                console.log("Admin logout - navigating to login with fromUrl state");
                set({
                  error,
                  navigateTo: {
                    state: finalFromUrl ? { fromUrl: finalFromUrl } : undefined,
                    target: "/access/login"
                  }
                });
              } else {
                // Caso normale: naviga alla destinazione specificata
                const targetUrl = buildUrlWithParams(redirect, params);
                set({
                  error,
                  navigateTo: {
                    state: finalFromUrl ? { fromUrl: finalFromUrl } : undefined,
                    target: targetUrl
                  }
                });
              }
            }
          }
        });
    },


    signOutAftereQrcodeValidation : (redirect: string | null = "/", isFromAdmin?: boolean) => {
      // get auth store data
      const authMode = get().mode;
      const error = get().error;

      // alternative would be to check each error type in the finally statements
      const isRefreshing = get().isRefreshing;
      const smartConfiguration = get().smartConfiguration;
      // remove local storage
      awsSignOut({
        authorization: get().session?.getAccessToken().getJwtToken() ?? null,
        baseUrl: basePath,
        baseUrlSSO: ssoBaseUrl,
        clientIdSSO: ssoClientId,
        deleteSession: !isRefreshing,
        domain: get().corporateInfo?.corporate_domain,
        mode: authMode,
        refreshToken: get().session?.getRefreshToken() ?? null,
        sessionToken: get().sessionToken
      })
        .finally(() => {
          // reset auth store data: the error and navigation will be handled as query params
          const storedUser = localStorage.getItem("storedUser");
          localStorage.clear();

          if (storedUser) {
            localStorage.setItem("storedUser", storedUser);
          }

          set({ ...defaultAuthState, qrCodeValidate: !error });
          // redirect to corporate domain for logout (if necessary)
          if (redirect) {
            const baseRedirect = (
              environment !== "LOCAL"
              && smartConfiguration?.urlCorporateDomain
              && window.location.host !== smartConfiguration.urlCorporateDomain
            ) ? `https://${smartConfiguration.urlCorporateDomain}/logout`
              : window.location.origin;
            const params = Object.entries({
              error: error?.message ?? null,
              navigate: window.location.pathname !== redirect ? redirect : null
            })
              .filter(([, value]) => value != null)
              .map(([label, value]) => `${label}=${value}`)
              .join("&");
            if (baseRedirect !== window.location.origin) {
              window.location.href = `${baseRedirect}${params.length ? `?${params}` : ""}`;
            } else {
              set({
                error,
                navigateTo: { target: redirect }
              });
              if (isFromAdmin) {
                window.location.href = "/";
              }
            }
          }
        });
    },
    switchToInitiative: async() => {
      // get auth store data
      const authMode = get().mode;
      const sessionToken = get().sessionToken||"";
      const smartConfiguration = get().smartConfiguration;
      //const accessToken = get().session?.getAccessToken().getJwtToken() ?? "";
      // const corporateId = (get().userData?.organization_id||"").toString();
      //  const userId = (get().userData?.id_user||"0").toString();
      // remove local storage
      awsSignOut({
        authorization: null,
        baseUrl: null,
        baseUrlSSO: null,
        clientIdSSO: null,
        deleteSession: false,
        domain: get().corporateInfo?.corporate_domain,
        mode: null,
        refreshToken: null,
        sessionToken: null,
        switchInitiative: true
      });
      set({
        ...defaultAuthState,
        fromUrl: get().fromUrl, // keep fromUrl when switch to initiative
        isLoading: true,
        spinnerLoginVisible: true,
        hasSeedSession:true // aggiunto nel refactorin dei Login page
      });

      // redirect to initiative
      if (
        environment !== "LOCAL"
        && smartConfiguration?.urlDomain
        && smartConfiguration?.urlCorporateDomain
        && sessionToken
      ) {
        const redirectUri = `https://${smartConfiguration.urlDomain}.${smartConfiguration.urlCorporateDomain}`;
        const redirectParams = `seed_session=${sessionToken}&mode=${authMode}`;
        let fromUrl = get().fromUrl;

        // Filtra fromUrl con path non validi e prova a estrarre URL validi annidati
        if (fromUrl && hasInvalidPath(fromUrl)) {

          // Prova a estrarre un fromUrl valido dai query parameters dell'URL non valido
          try {
            const invalidUrl = new URL(fromUrl);
            const nestedFromUrl = invalidUrl.searchParams.get("fromUrl");
            if (nestedFromUrl && !hasInvalidPath(nestedFromUrl)) {
              fromUrl = decodeURIComponent(nestedFromUrl);
            } else {
              fromUrl = null;
            }
          } catch (error) {
            fromUrl = null;
          }
        }

        // Se fromUrl nello store non è disponibile o non valido, prova a recuperarlo dai query parameters
        if (!fromUrl) {
          const currentUrlParams = new URLSearchParams(window.location.search);
          const fromUrlParam = currentUrlParams.get("fromUrl");
          if (fromUrlParam && !hasInvalidPath(fromUrlParam)) {
            fromUrl = decodeURIComponent(fromUrlParam);
            console.log("switchToInitiative - recovered valid fromUrl from query params:", fromUrl);
          }
        }

        if (fromUrl) {
          // TODO: verificare che base url sia identico a redirectUri (ripristinare fromUrl solo se l'utente può accedere a quell'iniziativa)
          const path = new URL(fromUrl).pathname;
          if (path && path !== "/") {
            window.location.href = `${redirectUri}${path}?${redirectParams}`;
            return;
          }
        }
        console.log({ testURL:`${redirectUri}?${redirectParams}` });
        window.location.href = `${redirectUri}?${redirectParams}`;

      }
    },
    /*** SET ERROR ***/
    setUnAuthorized: (error: boolean) => {
      set({
        isUnAuthorized: error
      });
    },

    /*** SET STATE LOADING ***/
    setSessionSeeding: (loading: boolean) => {
      set({
        hasSeedSession: loading
      });
    },
    setSpinnerLoginVisible: (loading: boolean) => {
      set({
        spinnerLoginVisible: loading
      });
    },

    /*** PASSWORD ***/
    changePassword: async (oldPassword, newPassword) => {
      try {
        set({ isLoading: true });
        const user = get().user;
        if (!user) {
          throw new Error("compromised auth state: user");
        }
        await awsChangePassword(user, oldPassword, newPassword);
        return true;
      } catch (error) {
        if (error === ERRORS.UNAUTHORIZED) {
          get().refreshSession();
        } else {
          // eslint-disable-next-line no-console
          console.error("signInSSO Error:", error);
          set({ error: getError(error)?.[0] ?? null });
        }
        return false;
      } finally {
        set({ isLoading: false });
      }
    },
    completeCustomChallenge: async (challengeResponse, clientMetaData, redirect, loadSpinner) => {
      const newState = { ...get() };
      const uuidQr = localStorage.getItem("uuid_qrcode");
      let spinnerLogin = loadSpinner;
      try {
        newState.isLoading = true;
        set({
          isLoading: newState.isLoading,
          spinnerLoginVisible: Boolean(loadSpinner)
        });
        const user = newState.user;
        if (!user) {
          throw new Error("compromised auth state: user");
        }
        newState.mode = AUTH_MODE.Cognito;
        newState.session = await awsCompleteCustomChallenge({

          challengeResponse,
          clientMetaData: clientMetaData,
          cognitoUser: user
        });
        newState.sessionToken = await getApiSessionToken({
          accessToken: newState.session.getAccessToken().getJwtToken(),
          authorization: newState.session.getIdToken().getJwtToken(),
          refreshToken: newState.session.getRefreshToken().getToken()
        });
        // await setApiPrivacyAgreements({ sessionToken: newState.sessionToken });
        // newState.agreedToConditions = true;

        const userData = await getApiUserData({
          accessToken: newState.session?.getAccessToken().getJwtToken(),
          sessionToken: newState.sessionToken
        });
        newState.userData = userData.user;
        newState.isImpersonation = userData.isImpersonation;
        newState.userInfo = await getApiUserInfo({
          accessToken: newState.session?.getAccessToken().getJwtToken(),
          sessionToken: newState.sessionToken,
          language: getLanguageForApi()
        });

        // eslint-disable-next-line no-console
        console.log("completeCustomChallenge: tentativo recupero corporateInfo per organization_id:", newState.userData?.organization_id);

        newState.corporateInfo = await getApiCorporateInfo({
          accessToken: newState.session?.getAccessToken().getJwtToken(),
          corporateId: newState.userData?.organization_id,
          sessionToken: newState.sessionToken
        });

        // eslint-disable-next-line no-console
        console.log("completeCustomChallenge: corporateInfo recuperato:", newState.corporateInfo ? "SUCCESS" : "FAILED");
        if (redirect) {
          newState.navigateTo = redirect;
        }

        if(uuidQr && newState.session?.getAccessToken().getJwtToken() &&
        newState.session.getIdToken().getJwtToken() &&
        newState.session.getRefreshToken().getToken()){
          newState.qrCodeValidate = await validateQrSession({
            uuid: uuidQr,
            authToken: newState.session.getAccessToken().getJwtToken(),
            idToken: newState.session.getIdToken().getJwtToken(),
            refreshToken: newState.session.getRefreshToken().getToken()
          });
        }

        //check se il portale è in MAINTEINANCE MODE GLOBALE , NESSUNO PROFLO PUO ACCEDERE TRANNE PO
        const maintenanceParams = {
          idToken: newState.session?.getAccessToken().getJwtToken() ?? "",
          sessionToken: newState.sessionToken,
          userRoles: userData?.user?.roles
        };
        const isMaintenanceEnabled = await getMaintenanceStatus(maintenanceParams);
        newState.maintenanceGlobal = isMaintenanceEnabled;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        console.error("completeCustomChallenge error details:", {
          error,
          errorType: error?.type,
          errorMessage: error?.message,
          challengeParameters: error?.challengeParameters
        });

        if (error.type === COGNITO_CHALLENGE.CUSTOM_CHALLENGE) {
          newState.notice =
            error?.challengeParameters?.tentative === "resend_code"
              ? i18n.t("err_msg_mfa_resend")
              : i18n.t("err_msg_mfa_remainder");
          spinnerLogin = false;
        } else if (error === ERRORS.UNAUTHORIZED) {
          get().refreshSession();
        } else {
          // Gestiamo più specificamente gli errori per evitare logout non necessari
          console.error("completeCustomChallenge: unexpected error", error);

          // Solo per errori davvero critici che richiedono logout
          const criticalErrors = [
            "Invalid session for the user",
            "compromised auth state"
          ];

          const isCriticalError = criticalErrors.some(criticalError =>
            error?.message?.includes(criticalError)
          );

          if (isCriticalError) {
            newState.error = getError(error)?.[0] ?? null;
            console.warn("Critical error detected, will logout:", error?.message);
          } else {
            // Per errori non critici, mostra solo il notice senza logout
            newState.notice = error?.message || i18n.t("err_msg_generic");
            console.warn("Non-critical error, showing notice only:", error?.message);
          }
          spinnerLogin = false;
        }
      } finally {
        // Logout automatico solo per errori critici
        if (newState.error) {
          console.warn("Performing automatic logout due to critical error:", newState.error.message);
          newState.error && set({ error: getError(newState.error)?.[0] ?? null });
          get().signOut("/access/login");
        } else {
          if(!uuidQr){
            if (
              newState.session && newState.sessionToken
                && newState.corporateInfo?.corporate_domain
            ) {
              setAdminCookie({
                accessToken: newState.session.getAccessToken().getJwtToken(),
                authorization: newState.session.getIdToken().getJwtToken(),
                domain: newState.corporateInfo.corporate_domain,
                sessionId: newState.sessionToken
              });
            } else if (newState.corporateInfo?.corporate_domain) {
              removeAdminCookie({ domain: newState.corporateInfo.corporate_domain });
            }
          }else{
            //LOGIN SMART TV SETTA MESSAGGIO
            get().signOutAftereQrcodeValidation();
          }

          // Verifica che corporateInfo sia presente prima del salvataggio
          if (!newState.corporateInfo && newState.session && newState.sessionToken && newState.userData?.organization_id) {
            // eslint-disable-next-line no-console
            console.warn("completeCustomChallenge: corporateInfo mancante, tento il recupero automatico prima del salvataggio");

            try {
              const recoveredCorporateInfo = await getApiCorporateInfo({
                accessToken: newState.session.getAccessToken().getJwtToken(),
                corporateId: newState.userData.organization_id,
                sessionToken: newState.sessionToken
              });
              newState.corporateInfo = recoveredCorporateInfo;
              // eslint-disable-next-line no-console
              console.log("completeCustomChallenge: corporateInfo recuperato con successo nel retry");
            } catch (retryError) {
              // eslint-disable-next-line no-console
              console.error("completeCustomChallenge: fallito anche il retry per corporateInfo:", retryError);
            }
          }

          newState.isLoading = false;
          newState.spinnerLoginVisible = Boolean(spinnerLogin);
          set(newState);
          throttledPersistAuthStore(newState, () => {
            get().signOut("/access/login", false, false);
          });
        }
      }
    },
    // completeNewPasswordChallenge: async(password, userAttributes, redirect) => {
    completeNewPasswordChallenge: async (password, userAttributes) => {
      const newState = { ...get() };
      try {
        newState.isLoading = true;
        set({ isLoading: newState.isLoading });
        const user = newState.user;
        if (!user) {
          throw new Error("compromised auth state: user");
        }
        newState.mode = AUTH_MODE.Cognito;
        // newState.session = await awsCompleteNewPasswordChallenge({
        //   cognitoUser: user,
        //   newPassword: password,
        //   sessionUserAttributes: userAttributes
        // });
        // newState.sessionToken = await getApiSessionToken({
        //   accessToken: newState.session.getAccessToken().getJwtToken(),
        //   authorization: newState.session.getIdToken().getJwtToken(),
        //   refreshToken: newState.session.getRefreshToken().getToken()
        // });
        // newState.userData = await getApiUserData({
        //   sessionToken: newState.sessionToken
        // });
        // newState.userInfo = await getApiUserInfo({
        //   sessionToken: newState.sessionToken
        // });
        // if (redirect) {
        //   newState.navigateTo = redirect;
        // }
        await awsCompleteNewPasswordChallenge({
          cognitoUser: user,
          newPassword: password,
          sessionUserAttributes: userAttributes
        });
      } catch (error) {
        if (error === ERRORS.UNAUTHORIZED) {
          get().refreshSession();
        } else {
          // eslint-disable-next-line no-console
          console.error("completeNewPasswordChallenge:", error);
          newState.error = getError(error)?.[0] ?? null;
        }
      } finally {
        if (newState.error) {
          newState.error && set({ error: getError(newState.error)?.[0] ?? null });
          get().signOut();
        } else {
          if (
            newState.session && newState.sessionToken
            && newState.corporateInfo?.corporate_domain
          ) {
            setAdminCookie({
              accessToken: newState.session.getAccessToken().getJwtToken(),
              authorization: newState.session.getIdToken().getJwtToken(),
              domain: newState.corporateInfo.corporate_domain,
              sessionId: newState.sessionToken
            });
          } else if (newState.corporateInfo?.corporate_domain) {
            removeAdminCookie({ domain: newState.corporateInfo.corporate_domain });
          }
          newState.isLoading = false;
          set(newState);
          throttledPersistAuthStore(newState, get().signOut);
        }
      }
    },
    confirmPassword: async (corporateId, username, newPassword, mfaCode) => {
      try {
        set({ isLoading: true });
        const { external_id: externalId } = await retrieveExternalIdByUsername({ corporateId, username });
        await awsConfirmPassword(externalId, newPassword, mfaCode);
      } catch (error) {
        if (error === ERRORS.UNAUTHORIZED) {
          get().refreshSession();
        } else {
          // eslint-disable-next-line no-console
          console.error("confirmPassword Error:", error);
          set({ error: getError(error)?.[0] ?? null });
        }
      } finally {
        set({ isLoading: false });
      }
    },
    forgotPassword: async (corporateId, username, resend) => {
      try {
        set({ isLoading: true });
        const { external_id: externalId } = await retrieveExternalIdByUsername({ corporateId, username });
        await awsForgotPassword(externalId);
      } catch (error) {
        if (error === ERRORS.UNAUTHORIZED) {
          get().refreshSession();
        } else {
          // eslint-disable-next-line no-console
          console.error("forgotPassword Error:", error);
          set({ error: getError(error)?.[0] ?? null });
        }
      } finally {
        set({ isLoading: false });
        if (resend) {
          set({ notice: "err_msg_mfa_resend" });
        }

      }
    },

    /*** REFRESH ***/
    refreshSession: async () => {
      const newState = { ...get() };
      if (newState.isRefreshing) return;
      try {
        newState.isLoading = true;
        newState.isRefreshing = true;
        set({
          isLoading: newState.isLoading,
          isRefreshing: newState.isRefreshing
        });
        if (!newState.session) {
          throw new Error("compromised auth state: session");
        }
        if (newState.mode === AUTH_MODE.Cognito) {
          await refreshSessionCognito(newState);
        } else if (newState.mode === AUTH_MODE.SSO) {
          await refreshSessionSSO(newState);
        } else {
          throw new Error("compromised auth state: mode");
        }

        if (newState.session && newState.sessionToken && newState.userData?.initiative_id != null) {
          const initiatives = newState.userInfo?.initiatives.map(item => item.id);
          const initiativeRes = await fetch(`${basePath}/sessions`, {
            body: JSON.stringify({
              initiativeId: String(newState.userData.initiative_id),
              initiatives,
              sessionId: newState.sessionToken
            }),
            headers: {
              "Authorization": newState.session.getIdToken().getJwtToken(),
              "AuthToken": newState.session.getAccessToken().getJwtToken(),
              "Content-type": "application/json",
              "RefreshToken": newState.session.getRefreshToken().getToken()
            },
            method: "POST"
          });
          if (initiativeRes.ok) {
            const data = await initiativeRes.json();
            if (data.sessionId) {
              newState.sessionToken = data.sessionId;
            }
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("refreshSession error: ", error);
        newState.error = getError(error)?.[0] ?? null;
      } finally {
        if (newState.error) {
          set({ error: null });
          get().signOut(null, false, true);
        } else {
          if (
            newState.session && newState.sessionToken
            && newState.corporateInfo?.corporate_domain
          ) {
            setAdminCookie({
              accessToken: newState.session.getAccessToken().getJwtToken(),
              authorization: newState.session.getIdToken().getJwtToken(),
              domain: newState.corporateInfo.corporate_domain,
              sessionId: newState.sessionToken
            });
          } else if (newState.corporateInfo?.corporate_domain) {
            removeAdminCookie({ domain: newState.corporateInfo.corporate_domain });
          }
          newState.isLoading = false;
          newState.isRefreshing = false;
          set(newState);

          // 🚨 GESTIONE ERRORE throttledPersistAuthStore con logout automatico in caso di errore
          throttledPersistAuthStore(newState, () => {
            get().signOut("/access/login", false, false);
          });
        }
      }
    },
    seedLogout: async (error, navigate) => {
      const newState = { ...get() };
      if (newState.isSeeding.logout) return;
      try {
        newState.isLoading = true;
        newState.isSeeding.logout = true;
        set({
          isLoading: newState.isLoading,
          isSeeding: newState.isSeeding
        });
        newState.error = error ? new Error(error) : defaultAuthState.error;
        newState.navigateTo = navigate ? { target: navigate } : defaultAuthState.navigateTo;
      } catch (error) {
        if (error === ERRORS.UNAUTHORIZED) {
          get().refreshSession();
        } else {
          // eslint-disable-next-line no-console
          console.error("seedSession error: ", error);
          newState.error = getError(error)?.[0] ?? null;
        }
      } finally {
        newState.error && set({ error: getError(newState.error)?.[0] ?? null });
        if (
          newState.session && newState.sessionToken
          && newState.corporateInfo?.corporate_domain
        ) {
          setAdminCookie({
            accessToken: newState.session.getAccessToken().getJwtToken(),
            authorization: newState.session.getIdToken().getJwtToken(),
            domain: newState.corporateInfo.corporate_domain,
            sessionId: newState.sessionToken
          });
        } else if (newState.corporateInfo?.corporate_domain) {
          removeAdminCookie({ domain: newState.corporateInfo.corporate_domain });
        }
        newState.isLoading = false;
        newState.isSeeding.logout = false;
        set(newState);
        throttledPersistAuthStore(newState, get().signOut);
      }
    },
    seedSession: async (mode, sessionToken) => {
      const newState = { ...get() };
      if (newState.isSeeding.session) return;
      try {
        newState.isLoading = true;
        newState.isSeeding.session = true;
        set({
          isLoading: newState.isLoading,
          isSeeding: newState.isSeeding
        });
        newState.sessionToken = sessionToken;
        const {
          authToken: access_token,
          idToken: id_token,
          refreshToken: refresh_token,
          // user
          userPool
        } = await apiSeedSession({
          baseUrl: basePath,
          sessionToken: newState.sessionToken
        });
        newState.mode = mode;
        // newState.user = user;
        newState.session = new CognitoUserSession({
          AccessToken: new CognitoAccessToken({ AccessToken: access_token }),
          IdToken: new CognitoIdToken({ IdToken: id_token }),
          RefreshToken: new CognitoRefreshToken({ RefreshToken: refresh_token })
        });
        newState.user = new CognitoUser({
          Pool: userPool,
          Username: newState.session.getIdToken().decodePayload()["cognito:username"]
        });
        newState.user.setSignInUserSession(newState.session);
        newState.user.setAuthenticationFlowType("CUSTOM_AUTH");

        const userData = await getApiUserData({
          accessToken: newState.session?.getAccessToken().getJwtToken(),
          sessionToken: newState.sessionToken
        });
        newState.userData = userData.user;
        newState.isImpersonation = userData.isImpersonation;
        newState.userInfo = await getApiUserInfo({
          accessToken: newState.session?.getAccessToken().getJwtToken(),
          sessionToken: newState.sessionToken,
          language: getLanguageForApi()
        });
        newState.corporateInfo = await getApiCorporateInfo({
          accessToken: newState.session?.getAccessToken().getJwtToken(),
          corporateId: newState.userData?.organization_id,
          sessionToken: newState.sessionToken
        });
        if (newState.userData.initiative_id !== null && newState.userData.organization_id !== null) {
          newState.smartConfiguration = await getSmartConfiguration({
            accessToken: newState.session.getAccessToken().getJwtToken(),
            corporateId: newState.userData.organization_id,
            initiativeId: newState.userData.initiative_id,
            sessionToken: newState.sessionToken
          });
        }

      } catch (error) {
        if (error !== ERRORS.UNAUTHORIZED) {
          // eslint-disable-next-line no-console
          console.error("seedSession error: ", error);
          newState.error = getError(error)?.[0] ?? null;
        }
      } finally {
        if (newState.error) {
          newState.error && set({ error: getError(newState.error)?.[0] ?? null });
          get().signOut();
        } else {
          if (
            newState.session && newState.sessionToken
            && newState.corporateInfo?.corporate_domain
          ) {
            setAdminCookie({
              accessToken: newState.session.getAccessToken().getJwtToken(),
              authorization: newState.session.getIdToken().getJwtToken(),
              domain: newState.corporateInfo.corporate_domain,
              sessionId: newState.sessionToken
            });
          } else if (newState.corporateInfo?.corporate_domain) {
            removeAdminCookie({ domain: newState.corporateInfo.corporate_domain });
          }
          newState.hasSeedSession = false;
          newState.isLoading = false;
          newState.isSeeding.session = false;
          set(newState);
          throttledPersistAuthStore(newState, get().signOut);
        }
      }
    },

    /*** REFETCH ***/
    refetchSmartConfiguration: async (force = false) => {
      const newState = { ...get() };
      try {
        set({ isLoading: true });
        if (!newState.smartConfiguration || force) {
          if (!newState.session) {
            throw new Error("compromised auth state: session");
          }
          if (!newState.sessionToken) {
            throw new Error("compromised auth state: sessionToken");
          }
          if (newState.userData?.initiative_id == null || newState.userData?.organization_id == null) {
            throw new Error("compromised auth state: userData");
          }
          const newSmartConfiguration = await getSmartConfiguration({
            accessToken: newState.session.getAccessToken().getJwtToken(),
            corporateId: newState.userData.organization_id,
            initiativeId: newState.userData.initiative_id,
            sessionToken: newState.sessionToken
          });
          set({ smartConfiguration: newSmartConfiguration });
        }
      } catch (error) {
        if (error !== ERRORS.UNAUTHORIZED) {
          // eslint-disable-next-line no-console
          console.error("refetchSmartConfiguration Error:", error);
          set({ error: getError(error)?.[0] ?? null });
        }
      } finally {
        set({ isLoading: false });
      }
    },
    refetchUserInfo: async () => {
      try {
        set({ isLoading: true });
        const sessionToken = get().sessionToken;
        const session = get().session;
        if (!sessionToken) {
          throw new Error("compromised auth state: sessionToken");
        }
        const newUserInfo = await getApiUserInfo({
          accessToken: session?.getAccessToken().getJwtToken() ?? "",
          sessionToken,
          language: getLanguageForApi()
        });
        set({ userInfo: newUserInfo });
      } catch (error) {
        if (error !== ERRORS.UNAUTHORIZED) {
          // eslint-disable-next-line no-console
          console.error("refetchUserInfo Error:", error);
          set({ error: getError(error)?.[0] ?? null });
        }
      } finally {
        const newState = { ...get() };
        newState.isLoading = false;
        set({ isLoading: false });
        set(newState);
        const username = newState.user?.getUsername();
        if (typeof username === "string") {
          throttledPersistAuthStore(newState, get().signOut);
        } else {
          //username is object with cognito session data
          throttledPersistAuthStore(username, get().signOut);
        }
      }
    },
    /*** RESET ***/
    // mainly to be used in error modals
    resetError: () => {
      // const params = new URLSearchParams(search);
      // params.delete("error");
      // const paramsString = params.toString();
      // const newPath = `${pathname}${paramsString.length > 0 ? "?" : ""}${paramsString}`;
      set({
        error: defaultAuthState.error
        // navigateTo: { target: newPath }
      });
    },
    // resetError: (redirect?: RouterNavigation) => {
    //   const newState: Partial<MutableAuthState> = {
    //     error: defaultAuthState.error
    //   };
    //   if (redirect?.target) (
    //     newState.navigateTo = {
    //       state: redirect.state,
    //       target: redirect.target
    //     }
    //   );
    //   set(newState);
    // },
    // purtroppo lo auth store non esiste dentro al router,
    // quindi non e' possibile usare useNavigate direttamente da qui
    resetNavigate: () => {
      set({
        navigateTo: defaultAuthState.navigateTo
      });
    },
    // mainly to be used in notice modals
    resetNotice: () => {
      set({ notice: defaultAuthState.notice });
    },
    resetSmartConfigurator: () => {
      const newState = { ...get() };
      newState.smartConfiguration = null;
      if (newState.userData?.initiative_id) {
        newState.userData.initiative_id = null;
      }
      set({ smartConfiguration: null, userData: newState.userData });
      throttledPersistAuthStore(newState, () => {
        get().signOut("/access/login", false, false);
      });
    },

    resetQrValidation: () => {
      set({ qrCodeValidate: false });
    }


  };
});

// note: the parameter is mutated instead of being returned
async function refreshSessionCognito(newState: MutableAuthState): Promise<void> {
  const idTokenJwt = newState.session?.getIdToken().getJwtToken();
  const refreshToken = newState.session?.getRefreshToken();
  if (!idTokenJwt || !refreshToken) {
    throw new Error("compromised auth state: couldn't fetch tokens from local cognito instance");
  }
  // cfr. https://stackoverflow.com/a/71795288
  // if (session && session?.isValid() && !newState.isRefreshing) {
  const refreshSession = await awsRefreshSessionCognito({ refreshToken });
  newState.mode = AUTH_MODE.Cognito;
  newState.session = new CognitoUserSession({
    AccessToken: refreshSession.getAccessToken(),
    IdToken: refreshSession.getIdToken(),
    RefreshToken: refreshToken
  });
  newState.user?.setSignInUserSession(newState.session);
  newState.sessionToken = await getApiSessionToken({
    accessToken: newState.session.getAccessToken().getJwtToken(),
    authorization: newState.session.getIdToken().getJwtToken(),
    refreshToken: newState.session.getRefreshToken().getToken()
  });
}

// note: the parameter is mutated instead of being returned
async function refreshSessionSSO(newState: MutableAuthState): Promise<void> {
  const refreshToken = newState.session?.getRefreshToken();
  if (!refreshToken) {
    throw new Error("compromised auth state: couldn't fetch tokens from local cognito instance");
  }
  const jwtTokens = await awsRefreshSessionSSO({
    baseUrl: ssoBaseUrl,
    clientId: ssoClientId,
    refreshToken
  });
  if (!jwtTokens) {
    throw new Error("compromised auth state: couldn't fetch jwt tokens from SSO");
  }
  newState.mode = AUTH_MODE.SSO;
  storeJwtTokens(jwtTokens);
  newState.session = new CognitoUserSession({
    AccessToken: new CognitoAccessToken({ AccessToken: jwtTokens.access_token }),
    IdToken: new CognitoIdToken({ IdToken: jwtTokens.id_token }),
    RefreshToken: refreshToken
  });
  newState.user?.setSignInUserSession(newState.session);
  newState.sessionToken = await getApiSessionToken({
    accessToken: newState.session.getAccessToken().getJwtToken(),
    authorization: newState.session.getIdToken().getJwtToken(),
    refreshToken: newState.session.getRefreshToken().getToken()
  });
}
